import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import type {
  TicketCategoryDTO,
  TicketPriorityDTO,
  TicketStatusDTO
} from '@/pages/Tickets';
import {
  ticketCategoriesAtom,
  ticketPrioritiesAtom,
  ticketStatusesAtom
} from '@/pages/Tickets/Tickets.store';

import { useHandleError } from './useHandleError';

export const useStaticData = () => {
  const [statuses, setStatuses] = useAtom(ticketStatusesAtom);
  const [priorities, setPriorities] = useAtom(ticketPrioritiesAtom);
  const [categories, setCategories] = useAtom(ticketCategoriesAtom);
  const handleError = useHandleError();

  const {
    error: statusesError,
    isFetching: statusesIsFetching,
    isSuccess: isStatusSuccess,
    isError: isStatusError,
    data: statusData
  } = useQuery<TicketStatusDTO[]>({
    queryKey: ['ticket_statuses/'],
    staleTime: Infinity
  });

  useEffect(() => {
    if (isStatusSuccess) {
      setStatuses(statusData);
    }
    if (isStatusError) {
      handleError(
        statusesError as AxiosError,
        'Failure to fetch ticket statuses.'
      );
    }
  }, [isStatusSuccess, isStatusError]);

  const {
    error: prioritiesError,
    isFetching: prioritiesIsFetching,
    isSuccess: isPrioritySuccess,
    isError: isPriorityError,
    data: priorityData
  } = useQuery<TicketPriorityDTO[]>({
    queryKey: ['ticket_priorities/'],
    staleTime: Infinity
  });

  useEffect(() => {
    if (isPrioritySuccess) {
      setPriorities(priorityData);
    }
    if (isPriorityError) {
      handleError(
        prioritiesError as AxiosError,
        'Failure to fetch ticket priorities.'
      );
    }
  }, [isPrioritySuccess, isPriorityError]);

  const {
    error: categoriesError,
    isFetching: categoriesIsFetching,
    isSuccess: isCategoriesSuccess,
    isError: isCategoriesError,
    data: categoriesData
  } = useQuery<TicketCategoryDTO[]>({
    queryKey: ['ticket_categories/'],
    staleTime: Infinity
  });

  useEffect(() => {
    if (isCategoriesSuccess) {
      setCategories(categoriesData);
    }
    if (isCategoriesError) {
      handleError(
        categoriesError as AxiosError,
        'Failure to fetch ticket categories.'
      );
    }
  }, [isCategoriesSuccess, isCategoriesError]);

  const isFetching =
    statusesIsFetching || prioritiesIsFetching || categoriesIsFetching;
  const error = statusesError || prioritiesError || categoriesError;

  return {
    statuses,
    priorities,
    categories,
    isFetching,
    error
  };
};

export default useStaticData;
